import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AiContexts } from './AiContexts';
import {
  aiSessionFilterCounts,
  aiSessionMatches,
  buildAiSessionCatalog,
  cleanupStateLabel,
  shortSessionId,
  type AiSessionAssignmentRecord,
  type AiSessionCatalogRow,
  type AiSessionFilter,
  type AiSessionNodeRecord,
  type AiSessionRecord,
  type AiSessionRunRecord,
  type AiSessionTaskRecord,
  type AiSessionWorkerRecord,
} from '../lib/ai-sessions';

const FILTERS: readonly { readonly key: AiSessionFilter; readonly label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'retired', label: 'Retired / Archived' },
  { key: 'replaced', label: 'Replaced' },
  { key: 'cleanup', label: 'Cleanup requested / failed' },
];

interface ProjectRef {
  readonly id: string;
  readonly name: string;
}

interface LoadState {
  readonly projectId: string | null;
  readonly rows: readonly AiSessionCatalogRow[];
  readonly status: 'idle' | 'loading' | 'ready' | 'error';
}

export function AiSessions({
  client,
  orgId,
  project,
  ownerVerified,
  onVerifyOwner,
}: {
  readonly client: SupabaseClient;
  readonly orgId: string;
  readonly project: ProjectRef | null;
  readonly ownerVerified: boolean;
  readonly onVerifyOwner: () => void;
}) {
  const [filter, setFilter] = useState<AiSessionFilter>('active');
  const [state, setState] = useState<LoadState>({ projectId: null, rows: [], status: 'idle' });

  useEffect(() => {
    if (orgId === '' || project === null) {
      setState({ projectId: null, rows: [], status: 'idle' });
      return;
    }

    let current = true;
    setState({ projectId: project.id, rows: [], status: 'loading' });
    const db = client.schema('brunel');

    void Promise.all([
      db.from('sessions')
        .select('id,project_id,worker_id,node_id,offering_key,generation,status,lifecycle,replacement_session_id,retired_at,provider_cleanup_state')
        .eq('org_id', orgId).eq('project_id', project.id).order('generation', { ascending: false }).limit(500),
      db.from('assignments')
        .select('id,run_id,session_id,active')
        .eq('org_id', orgId).eq('project_id', project.id).limit(500),
      db.from('runs')
        .select('id,task_id,status')
        .eq('org_id', orgId).eq('project_id', project.id).limit(500),
      db.from('tasks')
        .select('id,title,status')
        .eq('org_id', orgId).eq('project_id', project.id).limit(500),
      db.from('workers')
        .select('id,number,name')
        .eq('org_id', orgId).order('number').limit(200),
      db.from('node_presence')
        .select('node_id,name')
        .eq('org_id', orgId).order('name').limit(200),
    ]).then(([sessions, assignments, runs, tasks, workers, nodes]) => {
        if (!current) return;
        if (sessions.error || assignments.error || runs.error || tasks.error || workers.error || nodes.error) {
          setState({ projectId: project.id, rows: [], status: 'error' });
          return;
        }
        const rows = buildAiSessionCatalog({
          projectId: project.id,
          projectName: project.name,
          sessions: (sessions.data ?? []) as readonly AiSessionRecord[],
          assignments: (assignments.data ?? []) as readonly AiSessionAssignmentRecord[],
          runs: (runs.data ?? []) as readonly AiSessionRunRecord[],
          tasks: (tasks.data ?? []) as readonly AiSessionTaskRecord[],
          workers: (workers.data ?? []) as readonly AiSessionWorkerRecord[],
          nodes: (nodes.data ?? []) as readonly AiSessionNodeRecord[],
        });
        setState({ projectId: project.id, rows, status: 'ready' });
      })
      .catch(() => {
        if (current) setState({ projectId: project.id, rows: [], status: 'error' });
      });

    return () => { current = false; };
  }, [client, orgId, project?.id, project?.name]);

  if (project === null) {
    return (
      <section className="work one engine-room-sessions" aria-label="AI Sessions">
        <AiSessionsHeader projectName={null} />
        <p className="muted">Choose a project to read its AI sessions.</p>
      </section>
    );
  }

  if (state.status === 'loading' || state.projectId !== project.id) {
    return (
      <>
      <section className="work one engine-room-sessions" aria-label="AI Sessions">
        <AiSessionsHeader projectName={project.name} />
        <p className="muted">Reading the project’s recorded sessions…</p>
      </section>
      <AiContexts client={client} orgId={orgId} project={project} ownerVerified={ownerVerified} onVerifyOwner={onVerifyOwner} />
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
      <section className="work one engine-room-sessions" aria-label="AI Sessions">
        <AiSessionsHeader projectName={project.name} />
        <p role="status">AI Sessions could not be read. Existing sessions were not changed.</p>
      </section>
      <AiContexts client={client} orgId={orgId} project={project} ownerVerified={ownerVerified} onVerifyOwner={onVerifyOwner} />
      </>
    );
  }

  return <>
    <AiSessionsCatalog rows={state.rows} filter={filter} onFilter={setFilter} projectName={project.name} />
    <AiContexts client={client} orgId={orgId} project={project} ownerVerified={ownerVerified} onVerifyOwner={onVerifyOwner} />
  </>;
}

function AiSessionsHeader({ projectName }: { readonly projectName: string | null }) {
  return (
    <header className="engine-sessions-head">
      <div>
        <h2>AI Sessions</h2>
        <p className="muted">
          Recorded workforce-session lifecycle for {projectName ?? 'the selected project'}. This view is read-only.
        </p>
      </div>
      <span className="tag">No provider actions</span>
    </header>
  );
}

export function AiSessionsCatalog({
  rows,
  filter,
  onFilter,
  projectName,
}: {
  readonly rows: readonly AiSessionCatalogRow[];
  readonly filter: AiSessionFilter;
  readonly onFilter: (filter: AiSessionFilter) => void;
  readonly projectName: string;
}) {
  const counts = useMemo(() => aiSessionFilterCounts(rows), [rows]);
  const visible = useMemo(() => rows.filter((row) => aiSessionMatches(row, filter)), [rows, filter]);

  return (
    <section className="work one engine-room-sessions" aria-label="AI Sessions">
      <AiSessionsHeader projectName={projectName} />
      <div className="engine-session-filters" role="group" aria-label="AI Session filters">
        {FILTERS.map((item) => (
          <button
            type="button"
            key={item.key}
            className={filter === item.key ? 'active' : undefined}
            aria-pressed={filter === item.key}
            onClick={() => onFilter(item.key)}
          >
            <span>{item.label}</span>
            <strong>{counts[item.key]}</strong>
          </button>
        ))}
      </div>

      <p className="muted engine-session-boundary">
        These workforce sessions do not record provider-native ID, creation source or keep/disposable state. Observed AI contexts appear below when available.
      </p>

      {visible.length === 0 ? (
        <p className="muted">No recorded sessions match this filter.</p>
      ) : (
        <ul className="engine-session-list">
          {visible.map((row) => <AiSessionRow key={row.id} row={row} />)}
        </ul>
      )}
    </section>
  );
}

function AiSessionRow({ row }: { readonly row: AiSessionCatalogRow }) {
  return (
    <li>
      <article className="engine-session-card" data-lifecycle={row.lifecycle}>
        <header>
          <span>
            <strong>{row.workerLabel}</strong>
            <small className="muted">Generation {row.generation} · {row.projectName}</small>
          </span>
          <span className="status">{row.lifecycle.replaceAll('_', ' ')}</span>
        </header>
        <dl>
          <div><dt>Brunel session</dt><dd><code title={row.id}>{shortSessionId(row.id)}</code></dd></div>
          <div><dt>Computer</dt><dd>{row.nodeName}</dd></div>
          <div><dt>Configured AI</dt><dd><code>{row.offeringKey}</code></dd></div>
          <div><dt>Operational state</dt><dd>{row.status.replaceAll('_', ' ')}</dd></div>
          <div><dt>Task</dt><dd>{row.taskTitle ?? 'Unknown'}{row.taskStatus === null ? '' : ` · ${row.taskStatus.replaceAll('_', ' ')}`}</dd></div>
          <div><dt>Run</dt><dd>{row.runId === null ? 'Unknown' : <><code title={row.runId}>{shortSessionId(row.runId)}</code>{row.runStatus === null ? '' : ` · ${row.runStatus.replaceAll('_', ' ')}`}</>}</dd></div>
          <div><dt>Replacement</dt><dd>{row.replacementSessionId === null ? 'None recorded' : <code title={row.replacementSessionId}>{shortSessionId(row.replacementSessionId)}</code>}</dd></div>
          <div><dt>Retired</dt><dd>{retiredLabel(row.retiredAt)}</dd></div>
          <div><dt>Provider cleanup</dt><dd>{cleanupStateLabel(row.cleanupState)}</dd></div>
          <div><dt>Provider-native ID</dt><dd>Unknown</dd></div>
          <div><dt>Creation source</dt><dd>Unknown</dd></div>
          <div><dt>Keep / disposable</dt><dd>Unknown</dd></div>
        </dl>
      </article>
    </li>
  );
}

function retiredLabel(value: string | null): string {
  if (value === null) return 'Not retired';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}
